import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { AccountService, AlertService } from 'src/app/services/auth';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { ToastComponent } from 'src/app/shared/toast/toast.component';
import { ModalInfoComponent } from 'src/app/shared/modal-info/modal-info.component';

@Component({
    selector: 'app-Login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.componet.css'],
    standalone: false
})
export class LoginComponent implements OnInit {
  @ViewChild('toast', { static: false }) toast!: ToastComponent;
  @ViewChild('modalInfo', { static: true }) modalInfo!: ModalInfoComponent;

  btnDisable = true;
  loginForm: UntypedFormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  loggedIn: boolean;
  fieldTextType: boolean;
  etapa = 0;
  enumEtapa = {
    Login: 0,
    SelecionEmpresa: 1,
  };
  private useLocalLogin = false;

  modalTitle = 'Olvidaste tu contraseña';
  modalMessage = 'Contáctate con tu supervisor<br>para recuperarla.';

  vCard = `MECARD:N:xxxxx;TEL:123;;`;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private accountService: AccountService,
    private alertService: AlertService,
    private orderService: OrderService
  ) {
    if (this.accountService.userValue) {
      this.orderService.setValues(this.accountService.userValue.KEY);
      this.loggedIn = true;
    }
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      rut: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern(/^[0-9]{7,8}[0-9kK]$/)
      ]),
      password: ['', Validators.required],
      rutEmp: [''],
    });

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';

    if (this.accountService.userValue !== null) {
      this.orderService.setValues(this.accountService.userValue.KEY);
      this.router.navigate([this.returnUrl]);
    }
  }

  get f() {
    return this.loginForm.controls;
  }

  onSubmit() {
    this.submitted = true;

    // reset alerts
    this.alertService.clear();

    if (this.useLocalLogin) {
      this.loginLocal();
      return;
    }

    if (this.etapa === this.enumEtapa.SelecionEmpresa) {
      if (this.f.rutEmp.value.length > 1) {
        this.onIniciarSessionEmpresa();
      } else {
        this.onDisableButton();
      }
      return;
    }

    // Etapa login principal
    this.loading = true;
    this.accountService
      .loginPolpaico(
        this.normalizeRut(this.f.rut.value),
        this.f.password.value
      )
      .pipe(first())
      .subscribe(
        (data) => {
          if (data.length === 0) {
            this.toast.showToast('error', 'RUT o contraseña incorrectos.', 'Intenta nuevamente.');
            this.loading = false;
          } else {
            if (data.Estado) {
              // Pasa a seleccionar empresa
              this.etapa = this.enumEtapa.SelecionEmpresa;
              this.loading = false;
            } else {
              this.incrementTimesExpired();

              this.router.navigate([this.returnUrl]);
            }
          }
        },
        (error) => {
          console.error(error);
          this.handleLoginPrincipalHttpError(error);
          this.loading = false;
        }
      );
  }

  onIniciarSessionEmpresa() {
    this.loading = true;

    this.accountService
      .loginConRutEmpresaPolpaico(
        this.normalizeRut(this.f.rut.value),
        this.f.password.value,
        this.normalizeRut(this.f.rutEmp.value)
      )
      .pipe(first())
      .subscribe(
        (data) => {
          if (data.length === 0) {
            this.alertService.error('El Rut de la empresa es incorrecto.');
            this.loading = false;
          } else {
            this.incrementTimesExpired();

            this.router.navigate([this.returnUrl]);
          }
        },
        (error) => {
          this.handleLoginEmpresaHttpError(error);
          this.loading = false;
        }
      );
  }

  toggleFieldTextType() {
    this.fieldTextType = !this.fieldTextType;
  }

  onDisableButton() {
    this.btnDisable = this.f.rutEmp.value.length == 0;
  }

  private loginLocal() {
    const rut = this.f.rut.value || '11111111-1';
    const password = this.f.password.value || 'local';

    const dummyUser: any = {
      RUT_EMPRESA: '0',
      NOMBRE_EMPRESA: 'Polpaico',
      NOMBRE_USUARIO: 'Usuario Aridos',
      RUT_USUARIO: rut,
      FONO_USUARIO: '',
      MAIL_USUARIO: 'local@dev',
      ADM: 0,
      KEY: 'LOCAL_KEY',
      OBRAS: [],
      OBRASNOTIFICACIONES: [],
      CARGO: 'ARIDOS',
      ROL: 'ARIDOS',
      INTERNO: '0',
      VIAJES_M: [],
      ESTADO: '1',
      firma: '',
      OPERADOR_DE_PLANTA: '1',
      PLANTA_M: 'LOCAL'
    };

    localStorage.setItem('localMode', 'true');

    this.accountService.loginLocal(dummyUser, password);
    this.router.navigate(['/aridos/controlCombustible']);
  }

  private normalizeRut(value: string): string {
    return (value || '').replace(/[.]/g, '').replace(/[-]/g, '');
  }

  private incrementTimesExpired() {
    let timesExpired = Number(localStorage.getItem('timesExpired')) || 0;
    timesExpired++;
    localStorage.setItem('timesExpired', timesExpired.toString());
  }

  openForgotPassword() {
    this.modalInfo.open();
  }

  /**
   * Mantiene la UX legacy cuando el login va por API Gateway (401 no pasa por `data.length === 0`).
   */
  private handleLoginPrincipalHttpError(error: any): void {
    const status = error?.status;
    if (status === 401 || status === 403 || status === 400 || status === 423) {
      this.toast?.showToast(
        'error',
        'RUT o contraseña incorrectos.',
        'Intenta nuevamente.'
      );
      return;
    }
    this.alertService.error(
      'Actualmente se encuentra en mantención. Intente más tarde'
    );
  }

  private handleLoginEmpresaHttpError(error: any): void {
    const status = error?.status;
    if (status === 401 || status === 403 || status === 400 || status === 423) {
      this.toast?.showToast(
        'error',
        'El Rut de la empresa es incorrecto.',
        'Intenta nuevamente.'
      );
      this.alertService.error('El Rut de la empresa es incorrecto.');
      return;
    }
    this.alertService.error('Rut empresa inválido.');
  }
}

